import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/seo/SEO';
import { PostOffice } from '../types';
import { FaSearch, FaMapMarkerAlt, FaBuilding, FaDatabase, FaShieldAlt } from 'react-icons/fa';

const Home: React.FC = () => {
  const [results, setResults] = useState<PostOffice[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchType, setSearchType] = useState<'pincode' | 'area'>('pincode');

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Determine search type based on input
      const isPincode = /^\d+$/.test(searchQuery.trim());
      setSearchType(isPincode ? 'pincode' : 'area');

      const response = await fetch(
        isPincode
          ? `https://api.postalpincode.in/pincode/${searchQuery}`
          : `https://api.postalpincode.in/postoffice/${searchQuery}`
      );
      const data = await response.json();

      if (data && data[0] && data[0].Status === 'Success') {
        setResults(data[0].PostOffice || []);
      } else {
        setError('No results found. Please try a different search term.');
        setResults([]);
      }
    } catch (err) {
      setError('An error occurred while searching. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO
        pageType="static"
        title="Pincode Pro - Find Any Pincode in India"
        description="Search and find accurate pincode information for any location in India. Get instant access to post office details, area codes, and more."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Find Any Pincode in India
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-50">
              Quick, accurate, and comprehensive postal information at your fingertips
            </p>
          </motion.div>

          {/* Search Box */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Enter pincode or area name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                />
                <button
                  onClick={handleSearch}
                  disabled={isLoading}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                >
                  {isLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              {error && (
                <div className="mt-4 text-red-500 bg-red-100 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      {results.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Summary Text */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {searchType === 'area' ? (
                    <>
                      The Pincode for <span className="text-blue-600">{searchQuery}</span> is{' '}
                      <span className="text-blue-600">{results[0].Pincode}</span>
                    </>
                  ) : (
                    <>
                      Pincode <span className="text-blue-600">{searchQuery}</span> belongs to the following areas:
                    </>
                  )}
                </h2>
              </div>

              {/* Results Cards */}
              <div className="space-y-4">
                {results.map((office, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-100"
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-semibold text-gray-900">{office.Name}</h3>
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                              {office.BranchType}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600 gap-2">
                            <FaMapMarkerAlt className="text-gray-400" />
                            <span>{office.District}, {office.State}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-sm text-gray-600">Pincode</div>
                            <div className="text-2xl font-bold text-blue-600">{office.Pincode}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-8 text-center text-gray-600">
                <p>Found {results.length} {results.length === 1 ? 'result' : 'results'} for your search</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about PIN codes and our service
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
            {[
              {
                question: "What is a PIN code?",
                answer: "A PIN (Postal Index Number) code is a 6-digit number that uniquely identifies postal delivery areas across India. It helps in efficient mail sorting and delivery."
              },
              {
                question: "How accurate is the PIN code data?",
                answer: "Our PIN code data is regularly updated from official India Post sources and verified for accuracy. We maintain a 99.9% accuracy rate for all postal information."
              },
              {
                question: "Can I find a PIN code without knowing the exact area name?",
                answer: "Yes, you can search using partial area names, nearby landmarks, or browse through district/city listings to find the correct PIN code."
              },
              {
                question: "How often is the PIN code database updated?",
                answer: "Our database is updated weekly with the latest information from India Post. Any new PIN codes or changes are reflected within 7 days."
              },
              {
                question: "What information is provided with each PIN code?",
                answer: "Each PIN code entry includes the post office name, area details, district, state, and branch type (Head Office/Sub Office/Branch Office)."
              },
              {
                question: "Can I search multiple PIN codes at once?",
                answer: "Yes, our bulk search feature allows you to look up multiple PIN codes simultaneously. This is particularly useful for businesses."
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer relative"
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {faq.question}
                  </h3>
                  <div className="absolute inset-0 bg-white p-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex items-center">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Show More FAQs in Columns */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {[
              {
                question: "Is there an API available for PIN code lookups?",
                answer: "Yes, we offer a REST API for businesses to integrate PIN code lookups into their applications. Contact us for API access and documentation."
              },
              {
                question: "How do I report an incorrect PIN code?",
                answer: "You can report inaccuracies through our 'Report Error' feature on each PIN code result page or contact our support team directly."
              },
              {
                question: "What is the difference between a Head Post Office and Sub Post Office?",
                answer: "A Head Post Office is the main postal administrative center for an area, while Sub Post Offices are smaller branches that report to the Head Office."
              },
              {
                question: "Can I find nearby post offices using a PIN code?",
                answer: "Yes, entering a PIN code will show you all post offices within that postal area and nearby PIN codes with their respective post offices."
              },
              {
                question: "How do I find my area's PIN code using a landmark?",
                answer: "Use our search feature with a well-known landmark name, and we'll show you the PIN code and post offices serving that area."
              },
              {
                question: "Are PIN codes unique to each area?",
                answer: "Yes, each PIN code uniquely identifies a specific postal delivery area, though one area might be served by multiple PIN codes in some cases."
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer relative min-h-[120px]"
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {faq.question}
                  </h3>
                  <div className="absolute inset-0 bg-white p-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex items-center">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">Why Choose Pincode Pro?</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Experience the most comprehensive and reliable pincode lookup service in India
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-lg bg-white shadow-lg border border-gray-100"
            >
              <div className="text-blue-500 text-3xl mb-4"><FaSearch /></div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Instant Search</h3>
              <p className="text-gray-600">Lightning-fast search across all Indian postal codes and post offices.</p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-lg bg-white shadow-lg border border-gray-100"
            >
              <div className="text-blue-500 text-3xl mb-4"><FaDatabase /></div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Accurate Data</h3>
              <p className="text-gray-600">
                Up-to-date information synchronized with India Post database.
              </p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="p-6 rounded-lg bg-white shadow-lg border border-gray-100"
            >
              <div className="text-blue-500 text-3xl mb-4"><FaShieldAlt /></div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Reliable Service</h3>
              <p className="text-gray-600">Trusted by thousands of businesses and individuals daily.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">Coverage & Statistics</h2>
          <p className="text-center text-gray-600 mb-12">Comprehensive coverage across all Indian states and territories</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">155,000+</div>
              <div className="text-gray-600">Post Offices</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">29</div>
              <div className="text-gray-600">States Covered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">8</div>
              <div className="text-gray-600">Union Territories</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-gray-600">Coverage</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">Our Services</h2>
          <p className="text-center text-gray-600 mb-12">Comprehensive postal code services for all your needs</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-8 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100"
            >
              <div className="text-blue-500 text-3xl mb-4"><FaMapMarkerAlt /></div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Pincode Lookup</h3>
              <p className="text-gray-600">
                • Instant pincode search by area or post office name<br />
                • Detailed location information<br />
                • District and state mapping<br />
                • Postal circle details
              </p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-8 rounded-lg bg-gradient-to-br from-teal-50 to-teal-100"
            >
              <div className="text-teal-500 text-3xl mb-4"><FaBuilding /></div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Post Office Information</h3>
              <p className="text-gray-600">
                • Complete post office directory<br />
                • Branch type classification<br />
                • Delivery status information<br />
                • Operating hours and services
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Find Your Pincode?</h2>
          <p className="text-xl mb-8 text-blue-50">Get instant access to India's most comprehensive postal database</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold shadow-lg hover:bg-blue-50 transition-colors"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Search Now
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default Home;
